import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import CommentComposer from "@/components/forums/CommentComposer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMOJIS = ["🔥", "❤️", "💪", "🌈", "🥚"];

// Top-level comment (can have replies + reply composer)
export default function CommentItem({ comment, postId, locked, onChanged }) {
  const [showReply, setShowReply] = useState(false);
  const replies = comment.replies || [];

  return (
    <li data-testid={`forum-comment-${comment.comment_id}`} className="border-l-2 border-zinc-800 pl-4">
      <CommentBody
        comment={comment}
        canReply={!locked && !comment.parent_id}
        onToggleReply={() => setShowReply((s) => !s)}
        onChanged={onChanged}
      />
      {showReply && (
        <div className="mt-3">
          <CommentComposer
            postId={postId}
            parentId={comment.comment_id}
            placeholder={`Reply to ${comment.author?.name || "comment"}…`}
            autoFocus
            onPosted={() => { setShowReply(false); onChanged?.(); }}
          />
        </div>
      )}
      {replies.length > 0 && (
        <ul className="mt-3 space-y-3 pl-3 border-l border-zinc-900">
          {replies.map((r) => (
            <li key={r.comment_id} data-testid={`forum-comment-${r.comment_id}`}>
              <CommentBody comment={r} canReply={false} onChanged={onChanged} />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

// Renders the inner content of any comment (top-level or reply).
// Same layout for both; only differ in whether the Reply button appears.
function CommentBody({ comment, canReply, onToggleReply, onChanged }) {
  const { user } = useAuth();
  const [reactions, setReactions] = useState(comment.reactions || {});

  const react = async (emoji) => {
    if (!user) { window.dispatchEvent(new Event("omni:open-auth")); return; }
    try {
      const { data } = await axios.post(`${API}/forums/comments/${comment.comment_id}/react`, { emoji });
      setReactions(data.reactions);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not react");
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${API}/forums/comments/${comment.comment_id}`);
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    }
  };

  const canDelete = user && (user.role === "owner" || user.user_id === comment.user_id);

  return (
    <div>
      <CommentHeader author={comment.author} createdAt={comment.created_at} />
      <div className="text-neutral-200 whitespace-pre-wrap text-sm leading-relaxed">{comment.body}</div>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap">
        {EMOJIS.map((e) => {
          const n = reactions[e] || 0;
          return (
            <button
              key={e}
              data-testid={`forum-comment-react-${comment.comment_id}-${e}`}
              onClick={() => react(e)}
              className="inline-flex items-center gap-0.5 px-2 py-0.5 border border-zinc-800 hover:border-[#D4AF37] text-xs"
            >
              <span>{e}</span>
              {n > 0 && <span className="tabular-nums text-neutral-400 text-[10px]">{n}</span>}
            </button>
          );
        })}
        {canReply && (
          <button
            data-testid={`forum-comment-reply-${comment.comment_id}`}
            onClick={onToggleReply}
            className="text-[10px] uppercase tracking-widest text-neutral-500 hover:text-[#D4AF37] ml-2"
          >
            Reply
          </button>
        )}
        {canDelete && (
          <button
            data-testid={`forum-comment-delete-${comment.comment_id}`}
            onClick={del}
            className="text-[10px] uppercase tracking-widest text-[#DC2626] hover:text-white ml-auto inline-flex items-center gap-0.5"
          >
            <Trash2 size={10}/> Delete
          </button>
        )}
      </div>
    </div>
  );
}

function CommentHeader({ author, createdAt }) {
  if (!author) return null;
  const inner = (
    <span className="inline-flex items-center gap-1.5 text-xs">
      {author.picture && <img src={author.picture} alt="" className="w-4 h-4 rounded-full object-cover" />}
      <span className="text-white font-bold">{author.name}</span>
      {author.handle && <span className="text-neutral-600">@{author.handle}</span>}
      {author.is_owner && <span className="text-[9px] uppercase tracking-widest text-[#D4AF37] font-bold">Owner</span>}
    </span>
  );
  const link = author.public && author.handle
    ? <Link to={`/u/${author.handle}`} className="hover:text-[#D4AF37]">{inner}</Link>
    : inner;
  return (
    <div className="flex items-center gap-2 mb-1">
      {link}
      <span className="text-[10px] text-neutral-600 ml-auto">{new Date(createdAt).toLocaleString()}</span>
    </div>
  );
}
